import React, { useEffect, useState, useRef } from 'react';
import { X as CloseIcon } from '@phosphor-icons/react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Link } from 'react-router-dom';

const LOCALSTORAGE_KEY = 'ummahti_nonloggedin_overlay_last_shown';
const ELIGIBLE_SINCE_KEY = 'ummahti_nonloggedin_overlay_eligible_since';
const DELAY_MS = 10 * 60 * 1000; // 10 minutes

function wasShownToday() {
  const lastShown = localStorage.getItem(LOCALSTORAGE_KEY);
  if (!lastShown) return false;
  const lastDate = new Date(lastShown);
  const now = new Date();
  return (
    lastDate.getFullYear() === now.getFullYear() &&
    lastDate.getMonth() === now.getMonth() &&
    lastDate.getDate() === now.getDate()
  );
}

const NonLoggedInOverlay = ({ show }) => {
  const { isDarkMode } = useDarkMode();
  const [visible, setVisible] = useState(false);
  const timerRef = useRef();

  useEffect(() => {
    if (!show || wasShownToday()) {
      setVisible(false);
      return;
    }

    // Check or set eligibility timestamp
    let eligibleSince = localStorage.getItem(ELIGIBLE_SINCE_KEY);
    const now = Date.now();
    if (!eligibleSince) {
      localStorage.setItem(ELIGIBLE_SINCE_KEY, now.toString());
      eligibleSince = now;
    } else {
      eligibleSince = parseInt(eligibleSince, 10);
    }
    const elapsed = now - eligibleSince;
    if (elapsed >= DELAY_MS) {
      setVisible(true);
    } else {
      timerRef.current = setTimeout(() => setVisible(true), DELAY_MS - elapsed);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]);

  const handleClose = () => {
    localStorage.setItem(LOCALSTORAGE_KEY, new Date().toISOString());
    localStorage.removeItem(ELIGIBLE_SINCE_KEY);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm">
      <div
        className={`relative w-full max-w-md mx-4 rounded-2xl shadow-xl p-8 flex flex-col items-center text-center transition-colors duration-200
          ${isDarkMode ? 'bg-[#181f1b] text-white' : 'bg-white text-gray-900'}`}
        style={{ boxShadow: isDarkMode ? '0 8px 32px 0 rgba(0,0,0,0.45)' : '0 8px 32px 0 rgba(16,185,129,0.10)' }}
      >
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon size={22} weight="bold" />
        </button>
        <div className="mb-4">
          <img
            src={isDarkMode ? '/images/logo_white.png' : '/images/logo_green.png'}
            alt="Ummahti Logo"
            className="h-10 mx-auto mb-2"
          />
          <h2 className="text-2xl font-bold mb-2">We hope you're enjoying Ummahti!</h2>
          <p className="text-base mb-4 opacity-90">
            Register to save your progress across devices, sync your goals, and unlock more features.
          </p>
        </div>
        <Link
          to={window.isUmmahtiNative ? '/' : '/signup'}
          state={window.isUmmahtiNative ? { from: 'guestoverlay' } : null}
          className="inline-block px-6 py-2 rounded-full font-semibold text-white bg-emerald-500 hover:bg-emerald-600 shadow-md transition-colors"
        >
          Register Now
        </Link>
        <button
          className="mt-4 text-sm text-gray-500 dark:text-gray-400 underline hover:text-emerald-500"
          onClick={handleClose}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default NonLoggedInOverlay; 